import { ApiClient } from './client';
import type { ApiEnvelope, ApiResponse } from './types';
import { unwrapApiResponseData } from './types';
import { translateClient } from '@/lib/i18n/client';
import type {
  AdminDiscountCouponClaimsResponse,
  AdminDiscountCouponListResponse,
  AdminDiscountCouponRepairResult,
  AdminDiscountCouponResponse,
  AdminDiscountCouponSafetyReport,
  ClaimDiscountCouponResponse,
  CreateDiscountCouponPayload,
  PublicDiscountCouponResponse,
  UpdateDiscountCouponPayload,
  UserDiscountCouponClaimsResponse,
  } from '@/types/discount-coupons';

interface BackendEnvelope<T> {
  success: boolean;
  data: T;
}

interface BackendListEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

function unwrapEnvelope<T>(response: ApiResponse<BackendEnvelope<T> | T>): ApiResponse<T> {
  return unwrapApiResponseData<T>(response as ApiResponse<ApiEnvelope<T> | T>, {
    code: 'INVALID_RESPONSE',
    message: translateClient('errors', 'common.fallbackMessage'),
  });
}

class DiscountCouponsApi extends ApiClient {
  async getPublicCoupon(code: string): Promise<ApiResponse<PublicDiscountCouponResponse>> {
    const response = await this.request<BackendEnvelope<Omit<PublicDiscountCouponResponse, 'success'>>>(`/coupons/public/${encodeURIComponent(code)}`);
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, ...unwrapped.data! } };
  }

  async claimCoupon(code: string): Promise<ApiResponse<ClaimDiscountCouponResponse>> {
    const response = await this.request<BackendEnvelope<Omit<ClaimDiscountCouponResponse, 'success'>>>('/coupons/claim', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, ...unwrapped.data! } };
  }

  async listMyAvailableClaims(): Promise<ApiResponse<UserDiscountCouponClaimsResponse>> {
    const response = await this.request<BackendEnvelope<{ available: UserDiscountCouponClaimsResponse['claims'] }>>('/coupons/me/available');
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, claims: unwrapped.data?.available ?? [] } };
  }
}

class AdminDiscountCouponsApi extends ApiClient {
  async listCoupons(): Promise<ApiResponse<AdminDiscountCouponListResponse>> {
    const response = await this.request<BackendListEnvelope<AdminDiscountCouponListResponse['coupons'][number]>>('/admin/coupons');
    if (response.success !== true || response.error) return { success: false, error: response.error };
    return { success: true, data: { success: true, coupons: response.data?.items ?? [] } };
  }

  async getSafetyReport(params?: { maxAgeMinutes?: number; couponId?: string }): Promise<ApiResponse<AdminDiscountCouponSafetyReport>> {
    const query = new URLSearchParams();
    if (params?.maxAgeMinutes) query.set('max_age_minutes', String(params.maxAgeMinutes));
    if (params?.couponId) query.set('coupon_id', params.couponId);
    const suffix = query.toString();
    const response = await this.request<BackendEnvelope<Omit<AdminDiscountCouponSafetyReport, 'success'>>>(`/admin/coupons/safety-report${suffix ? `?${suffix}` : ''}`);
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, ...unwrapped.data! } };
  }

  async repairStaleReservations(payload?: { maxAgeMinutes?: number; couponId?: string }): Promise<ApiResponse<AdminDiscountCouponRepairResult>> {
    const response = await this.request<BackendEnvelope<Omit<AdminDiscountCouponRepairResult, 'success'>>>('/admin/coupons/repair-stale-reservations', {
      method: 'POST',
      body: JSON.stringify({
        max_age_minutes: payload?.maxAgeMinutes ?? 30,
        coupon_id: payload?.couponId,
      }),
    });
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, ...unwrapped.data! } };
  }

  async createCoupon(payload: CreateDiscountCouponPayload): Promise<ApiResponse<AdminDiscountCouponResponse>> {
    const response = await this.request<BackendEnvelope<AdminDiscountCouponResponse['coupon']>>('/admin/coupons', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, coupon: unwrapped.data! } };
  }

  async updateCoupon(couponId: string, payload: UpdateDiscountCouponPayload): Promise<ApiResponse<AdminDiscountCouponResponse>> {
    const response = await this.request<BackendEnvelope<AdminDiscountCouponResponse['coupon']>>(`/admin/coupons/${encodeURIComponent(couponId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    const unwrapped = unwrapEnvelope(response);
    if (unwrapped.error) return { success: false, error: unwrapped.error };
    return { success: true, data: { success: true, coupon: unwrapped.data! } };
  }

  async listClaims(couponId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<AdminDiscountCouponClaimsResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const suffix = query.toString();
    const response = await this.request<BackendListEnvelope<AdminDiscountCouponClaimsResponse['claims'][number]>>(`/admin/coupons/${encodeURIComponent(couponId)}/claims${suffix ? `?${suffix}` : ''}`);
    if (response.success !== true || response.error) return { success: false, error: response.error };
    return { success: true, data: { success: true, claims: response.data?.items ?? [], total: response.data?.total ?? 0 } };
  }
}

export const discountCouponsApi = new DiscountCouponsApi();
export const adminDiscountCouponsApi = new AdminDiscountCouponsApi();
